import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { useRef } from 'react';

import {
  Box,
  ButtonRadio,
  ButtonRadioProps,
  Icon,
  IconSwissFlag,
  LabelVatFree,
  Paper,
  SkeletonTypography,
  SkeletonWrapper,
  Typography,
} from '@core/component';
import { Translate } from '@core/constant';
import { useContextCurrency } from '@core/context';
import { getCHFValueFromPricingKey, useCurrencyRate, useLanguage } from '@core/hook';
import {
  EActionAttribute,
  EColor,
  EFontWeight,
  EIcon,
  EMetal,
  EPricing,
  ESize,
  ETypographyVariant,
  EZIndexName,
} from '@core/type';
import { formatCurrency } from '@core/util';

import { BadgeProductPrice } from './BadgeProductPrice';
import { BadgeWhatIsVatFreeStorage } from './BadgeWhatIsVatFreeStorage';
import { ProductLogisticBenefit } from './ProductLogisticBenefit';
import { TextProductFees } from './TextProductFees';
import { LOGISTIC_RADIO_TRANSITION, STORE_IN_VAULT_INPUT_ID } from './constants';
import { ForecastedDeliveryProps, RadioWrapperProps } from './interface-product-show';
import { getDealIndicator } from './utils';

export const RadioStoreInVault = ({
  isActive,
  product,
  productCount,
  forecastedDeliveryData,
  isForecastedDataLoading,
  ...restProps
}: ForecastedDeliveryProps &
  ButtonRadioProps &
  Pick<RadioWrapperProps, 'isActive' | 'product' | 'productCount' | 'forecastedDeliveryData'>) => {
  const benefitsRef = useRef<HTMLDivElement>(null);
  const { language } = useLanguage();
  const { currency } = useContextCurrency();
  const { getConvertedPriceFromCHF } = useCurrencyRate();
  const { t: tProductShow } = useTranslation(Translate.page.PRODUCT_SHOW);

  return (
    <Box width={'350px'}>
      <Paper
        isOutlined
        borderColor={isActive ? EColor.SECONDARY : EColor.PRIMARY}
        position={'relative'}
        zIndex={EZIndexName.OVER_DEFAULT}
        borderRadius={'10px'}
        backgroundColor={isActive ? EColor.INFO : undefined}
        transition={LOGISTIC_RADIO_TRANSITION}
      >
        <Box position={'absolute'} right={'30px'} top={'-12px'}>
          <LabelVatFree />
        </Box>
        <ButtonRadio
          wrapperProps={{ padding: '24px', alignItems: 'flex-start' }}
          circleProps={{ marginTop: '7px' }}
          id={STORE_IN_VAULT_INPUT_ID}
          {...restProps}
          label={
            <Box>
              <Box marginBottom={'10px'}>
                <Typography variant={ETypographyVariant.H5} as={EActionAttribute.SPAN}>
                  <Trans
                    i18nKey={`${Translate.page.PRODUCT_SHOW}:storedInOurVault`}
                    components={{ icon: <IconSwissFlag /> }}
                  />
                </Typography>
              </Box>
              <BadgeProductPrice isActive={isActive} {...product} />

              <TextProductFees
                indicator={getDealIndicator(product.indicator)}
                productType={product.productType}
              />

              <Box
                maxHeight={isActive ? '230px' : '0px'}
                overflow={'hidden'}
                transition={LOGISTIC_RADIO_TRANSITION}
              >
                <Box
                  paddingTop={'16px'}
                  ref={benefitsRef}
                  display="flex"
                  flexDirection="column"
                  gap="8px"
                >
                  {product.metalIso !== EMetal.GOLD && (
                    <BadgeWhatIsVatFreeStorage>
                      <ProductLogisticBenefit title={tProductShow('benefits.vatFreeProduct')} />
                    </BadgeWhatIsVatFreeStorage>
                  )}
                  <ProductLogisticBenefit
                    title={tProductShow('benefits.freeStorageUpTo10K', {
                      amount: formatCurrency(
                        language,
                        currency,
                        getConvertedPriceFromCHF(
                          EPricing.MANUAL_RATE,
                          getCHFValueFromPricingKey(EPricing.STORAGE_10K),
                        ),
                        { maximumFractionDigits: 0 },
                      ),
                    })}
                  />
                  <ProductLogisticBenefit title={tProductShow('benefits.freeAndInstantResale')} />
                  <ProductLogisticBenefit title={tProductShow('benefits.deliverAtAnyTime')} />
                </Box>
              </Box>
            </Box>
          }
        />
      </Paper>
      <Paper
        isOutlined
        borderColor={EColor.PRIMARY}
        transition={LOGISTIC_RADIO_TRANSITION}
        marginTop={isActive ? '-64px' : '-26px'}
        maxHeight={isActive ? '64px' : undefined}
        opacity={isActive ? '0' : '1'}
        padding={'32px 24px 6px'}
        backgroundColor={{ semanticType: EColor.SECONDARY, variant: EColor.LIGHT }}
        position={'relative'}
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        borderRadius={'10px'}
        htmlFor={STORE_IN_VAULT_INPUT_ID}
        as={EActionAttribute.LABEL}
        cursor={'pointer'}
      >
        {isForecastedDataLoading ? (
          <SkeletonWrapper width={'100%'}>
            <SkeletonTypography lineHeight={'24px'} />
          </SkeletonWrapper>
        ) : (
          <>
            <Icon type={EIcon.ARROW_TURNED} color={EColor.ACCENT} size={ESize.MD} />
            <Typography
              variant={ETypographyVariant.BODY3}
              textDecoration={'underline'}
              fontStyle={'italic'}
              color={EColor.ACCENT}
            >
              <Trans
                i18nKey={`${Translate.page.PRODUCT_SHOW}:benefits.saveByStoringWithUs`}
                values={{
                  amount: formatCurrency(
                    language,
                    forecastedDeliveryData.currencyIso,
                    forecastedDeliveryData.storageSaving,
                  ),
                }}
                components={{
                  strong: (
                    <Typography
                      variant={ETypographyVariant.BODY3}
                      fontWeight={EFontWeight.BOLD}
                      fontStyle={'italic'}
                      as={EActionAttribute.SPAN}
                    />
                  ),
                }}
              />
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};
